import { useMutation } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { companiesQueryKeys } from '@/modules/companies/queries/queryKeys';
import { useCurrentTenantQuery } from '@/modules/companies/queries/useCurrentTenantQuery';
import { endpoints } from '@/modules/signup-flow/constants';
import type { ApiRequestBody } from '@/types/utils/request';
import { invalidateQuery } from '@/utils/queries';

export type SignUpFlowApi = ApiRequestBody<
  typeof endpoints.updateSignupFlow,
  'put'
>;

export const useUpdateSignupFlowMutation = () => {
  const currentTenantQuery = useCurrentTenantQuery();

  return useMutation({
    mutationFn: (body: SignUpFlowApi) => {
      invariant(currentTenantQuery.data?.id, 'Company ID is required');

      return fetchClient.PUT(endpoints.updateSignupFlow, {
        params: { path: { id: currentTenantQuery.data.id } },
        body: {
          state: {
            ...currentTenantQuery.data?.onboarding?.state,
            ...body.state,
          },
          step: body.step ?? currentTenantQuery.data?.onboarding?.step,
          isComplete:
            body.isComplete ?? currentTenantQuery.data?.onboarding?.isComplete,
        },
      });
    },
    onSuccess: () => {
      // invalidate the company query to go to the next sign-up flow step
      const currentTenantQueryKey = companiesQueryKeys.currentTenant();
      invalidateQuery(currentTenantQueryKey)();
    },
  });
};
